import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { codeOf } from '../../game/shortcuts';

/**
 * A conversation with an NPC.
 *
 * THE KEYBOARD HANDOFF IS THE WHOLE PROBLEM, exactly as it is for ChatPanel.
 * Movement keys are bound to `window` and W/A/S/D call `preventDefault()`, so
 * while this is open the game must stop listening — otherwise you walk away
 * mid-sentence, and the number keys picking an answer also drive your
 * character. `onFocusChange` is that handshake.
 *
 * Escape closes, matching chat. That reflex already exists, so borrowing it
 * costs nothing to learn.
 *
 * The panel is a pure view: it holds no conversation state at all. Every choice
 * asks the server for the next node, which is what keeps traversal stateless —
 * see npcDialogue.js for why that is acceptable while dialogue is flavour, and
 * what would have to change if it ever unlocked anything.
 */

const CLOSE_KEY = codeOf('chatClose');

function DialoguePanel({
  speaker, node, onChoose, onClose,
}) {
  // Claim the keyboard for as long as the panel is up, and hand it back on the
  // way out — including when the component unmounts because the conversation
  // ended, which is the case that is easy to forget and leaves the game
  // permanently unresponsive.
  useEffect(() => {
    onClose.setInputEnabled(false);
    return () => onClose.setInputEnabled(true);
  }, [onClose]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === CLOSE_KEY) {
        event.preventDefault();
        onClose.close();
        return;
      }

      // Number keys pick an answer, so a conversation can be held without
      // reaching for the mouse.
      const index = Number(event.key) - 1;
      if (!Number.isInteger(index) || index < 0) return;

      if (node.choices[index]) {
        event.preventDefault();
        onChoose(node.choices[index]);
        return;
      }

      // The last line of a conversation offers no choices, and renders a button
      // labelled "1. Goodbye". Without this, that 1 does nothing — the panel
      // numbers an action and then declines to honour the number, which reads
      // as the keyboard being broken rather than as a missing case.
      if (index === 0 && node.choices.length === 0) {
        event.preventDefault();
        onClose.close();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [node, onChoose, onClose]);

  return (
    /*
      `pointer-events-auto` for the same reason ChatPanel carries it: the
      wrapper above is `pointer-events-none` so clicks reach the world, and a
      panel that does not opt back in never receives any.

      Its absence is what made clicking an answer walk your character instead
      of picking it — the click passed through to the canvas, which is the
      thing listening underneath. Silent, and it looks like the answer buttons
      are simply dead.
    */
    <div className="pointer-events-auto w-full max-w-2xl rounded-lg border border-gray-700 bg-gray-900/95 p-4">
      <p className="text-xs uppercase tracking-wide text-amber-400">{speaker}</p>
      <p className="mt-1 text-gray-100">{node.text}</p>

      <div className="mt-3 flex flex-col gap-1">
        {node.choices.map((choice, i) => (
          <button
            key={choice.label}
            type="button"
            onClick={() => onChoose(choice)}
            className="rounded bg-gray-800 px-3 py-2 text-left text-sm text-gray-100 hover:bg-gray-700"
          >
            <span className="mr-2 text-gray-500">
              {i + 1}
              .
            </span>
            {choice.label}
          </button>
        ))}

        {node.choices.length === 0 && (
          <button
            type="button"
            onClick={onClose.close}
            className="rounded bg-gray-800 px-3 py-2 text-left text-sm text-gray-300 hover:bg-gray-700"
          >
            <span className="mr-2 text-gray-500">1.</span>
            Goodbye
          </button>
        )}
      </div>

      <p className="mt-2 text-xs text-gray-600">Number keys to answer · Esc to walk away</p>
    </div>
  );
}

DialoguePanel.propTypes = {
  speaker: PropTypes.string.isRequired,
  node: PropTypes.shape({
    text: PropTypes.string,
    choices: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string,
      next: PropTypes.string,
    })),
  }).isRequired,
  onChoose: PropTypes.func.isRequired,
  /** `close` ends the conversation; `setInputEnabled` is the keyboard handshake. */
  onClose: PropTypes.shape({
    close: PropTypes.func.isRequired,
    setInputEnabled: PropTypes.func.isRequired,
  }).isRequired,
};

export default DialoguePanel;
